import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAdd,
  faTimes,
  faPenToSquare,
  faRefresh,
} from '@fortawesome/pro-regular-svg-icons';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useTranslationHook } from '../../../../hooks/UseTranslationHook';

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

// Function to calculate gradient color based on SLA value
const getGradientColor = (sla) => {
  const green = [0, 255, 0]; // RGB for green
  const red = [255, 0, 0]; // RGB for red

  // Calculate interpolation factor
  const factor = sla / 100;

  // Interpolate between green and red
  const r = Math.round(green[0] + factor * (red[0] - green[0]));
  const g = Math.round(green[1] + factor * (red[1] - green[1]));
  const b = Math.round(green[2] + factor * (red[2] - green[2]));

  return `rgb(${r}, ${g}, ${b})`;
};

const SlaLogsModal = ({ visitId, showModal, setShowModal, refreshData }) => {
  const [isFetching, setIsFetching] = useState(false);
  const [slaLogs, setSlaLogs] = useState([]);

  const {
    addString,
    editString,
    deleteString,
    statusString,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
    notesString,
  } = useTranslationHook();

  const initialColumns = [
    { field: 'id', headerName: 'ID', width: LOCAL_COLUMN_WIDTH },
    {
      field: 'event_type',
      headerName: 'Tipo de evento',
      width: LOCAL_COLUMN_WIDTH,
      renderCell: (params) => (
        <div style={{ fontWeight: 'bold' }}>
          <span>{params.row.event_type}</span>
        </div>
      ),
    },
    {
      field: 'ticket_date',
      headerName: 'Fecha y hora del ticket',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'commitment_date',
      headerName: 'Fecha de vencimiento',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'sla_hours',
      headerName: 'SLA (hrs)',
      width: 150,
    },
    {
      field: 'remaining_timestamp',
      headerName: 'Tiempo restante (hrs)',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'sla',
      headerName: 'SLA (%)',
      width: 150,
      renderCell: (params) => {
        const sla = new Number(params.row.sla).toFixed(2);
        const color = getGradientColor(sla);

        return (
          <div style={{ color: color, fontWeight: 'bold' }}>
            <span>{sla}%</span>
          </div>
        );
      },
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'created_by_name',
      headerName: createdByString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'updated_by_name',
      headerName: updatedByString,
      width: LOCAL_COLUMN_WIDTH,
    },
  ];

  const fetchSlaLogs = async () => {
    const endpoint = `${GlobalVariables.getURL()}sla/logs/${visitId}`;
    const response = await customAxios.get(endpoint, HEADERS);

    return response.data.data;
  };

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const [logsData] = await Promise.all([fetchSlaLogs()]);

      setSlaLogs(logsData);
    } catch (error) {
      console.log(error);
      getToast({ status: error, title: error.response.data.message });
    } finally {
      setIsFetching(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    refreshData(); // Refresh failure types data
  };

  useEffect(() => {
    if (!showModal) return;

    fetchData();
  }, [showModal]);

  return (
    <Dialog open={showModal} maxWidth='xl' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Historial de Tiempos
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <Grid container spacing={0} justifyContent='flex-end'>
          <Grid item>
            <Button
              size='small'
              variant='outlined'
              endIcon={<FontAwesomeIcon icon={faRefresh} />}
              onClick={async () => await fetchData()}
            >
              <Typography fontSize={14}>Actualizar</Typography>
            </Button>
          </Grid>
        </Grid>

        <DataGridPro
          rows={slaLogs}
          columns={initialColumns}
          loading={isFetching}
          initialState={{
            sorting: {
              sortModel: [{ field: 'id', sort: 'desc' }],
            },
            columns: {
              columnVisibilityModel: {
                id: false,
              },
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Stack height='100%' alignItems='center' justifyContent='center'>
                No hay historial de SLA
              </Stack>
            ),
            toolbar: GridToolbar,
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default SlaLogsModal;
